import React, { useState } from "react";
import api from "../../../../api/client";
import AssetModal, { Field } from "./AssetModal";
import { errorMessage, machineLabel, toInputDate } from "./assetUtils";

const PRESETS = [
  { label: "Daily", type: "days", value: 1 },
  { label: "Weekly", type: "days", value: 7 },
  { label: "Monthly", type: "days", value: 30 },
  { label: "Quarterly", type: "days", value: 90 },
  { label: "Yearly", type: "days", value: 365 },
  { label: "Every 250 h", type: "hours", value: 250 },
  { label: "Every 500 h", type: "hours", value: 500 },
];

// A preventive maintenance schedule for one machine.
export default function ScheduleModal({ schedule, machines, machineId, checklists, people, onClose, onSaved }) {
  const editing = Boolean(schedule);
  const [form, setForm] = useState({
    machineId: schedule?.machine?.id || machineId || "",
    title: schedule?.title || "",
    description: schedule?.description || "",
    intervalType: schedule?.intervalType || "days",
    intervalValue: schedule?.intervalValue ?? 30,
    firstDueAt: toInputDate(schedule?.nextDueAt) || "",
    checklistId: schedule?.checklist?.id || "",
    assigneeId: schedule?.assignee?.id || "",
    isActive: schedule ? schedule.isActive : true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const machine = machines.find((m) => m.id === form.machineId);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        machineId: form.machineId,
        title: form.title,
        description: form.description,
        intervalType: form.intervalType,
        intervalValue: Number(form.intervalValue),
        checklistId: form.checklistId || null,
        assigneeId: form.assigneeId || null,
        isActive: form.isActive,
      };
      if (form.intervalType === "days" && form.firstDueAt) {
        body[editing ? "nextDueAt" : "firstDueAt"] = new Date(`${form.firstDueAt}T09:00:00`).toISOString();
      }
      const res = editing
        ? await api.patch(`/manufacturing/assets/pm-schedules/${schedule.id}`, body)
        : await api.post("/manufacturing/assets/pm-schedules", body);
      onSaved(res.data.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save the schedule."));
      setBusy(false);
    }
  };

  return (
    <AssetModal
      title={editing ? "Edit preventive maintenance" : "New preventive maintenance"}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={editing ? "Save changes" : "Create schedule"}
    >
      <Field label="Machine *">
        <select className="am-select" value={form.machineId} onChange={set("machineId")} required disabled={editing || Boolean(machineId)}>
          <option value="">— Choose a machine —</option>
          {machines.filter((m) => m.status !== "retired").map((m) => <option key={m.id} value={m.id}>{machineLabel(m)}</option>)}
        </select>
      </Field>
      <Field label="Title *">
        <input className="am-input" value={form.title} onChange={set("title")} required maxLength={120} placeholder="e.g. Weekly lubrication & inspection" />
      </Field>

      <Field label="How often">
        <div className="am-filters" style={{ margin: 0 }}>
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className={`am-btn am-btn-sm ${form.intervalType === p.type && Number(form.intervalValue) === p.value ? "am-btn-primary" : "am-btn-ghost"}`}
              onClick={() => setForm((f) => ({ ...f, intervalType: p.type, intervalValue: p.value }))}
            >
              {p.label}
            </button>
          ))}
        </div>
      </Field>
      <div className="am-row">
        <Field label="Every">
          <input className="am-input" type="number" min="1" step="1" value={form.intervalValue} onChange={set("intervalValue")} required />
        </Field>
        <Field label="Unit">
          <select className="am-select" value={form.intervalType} onChange={set("intervalType")}>
            <option value="days">Days</option>
            <option value="hours">Running hours</option>
          </select>
        </Field>
      </div>
      {form.intervalType === "days" ? (
        <Field label={editing ? "Next due on" : "First due on"} hint={editing ? undefined : "Leave blank to start one interval from today."}>
          <input className="am-input" type="date" value={form.firstDueAt} onChange={set("firstDueAt")} />
        </Field>
      ) : (
        <div className="am-hint">
          Counted from the machine's hour meter{machine?.runningHours != null ? ` (now ${machine.runningHours} h)` : ""}. Keep the meter up to date on the machine, or enter the reading when logging maintenance.
        </div>
      )}

      <div className="am-row">
        <Field label="Checklist" hint="Filled in the app when the PM is completed">
          <select className="am-select" value={form.checklistId} onChange={set("checklistId")}>
            <option value="">— No checklist —</option>
            {checklists.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Reminder goes to" hint="Daily at 9am while due">
          <select className="am-select" value={form.assigneeId} onChange={set("assigneeId")}>
            <option value="">The machine's technician</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.reachable ? "" : " (no chat)"}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Instructions (optional)">
        <textarea className="am-textarea" value={form.description} onChange={set("description")} maxLength={2000} />
      </Field>
      {editing && (
        <label className="am-check">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
          Active — uncheck to pause reminders
        </label>
      )}
    </AssetModal>
  );
}
